import React, { useEffect, useState, useCallback, useContext } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import axios from 'axios';
import confetti from 'canvas-confetti';
import { toast } from 'sonner';
import { Lock, Check, ShieldCheck } from 'lucide-react';
import { useAuth } from '../../context/AuthContext';
import { useFunnelConfig } from './ThreeJobs';
import { PageCtx, useFunnelContent, sectionProps, renderRich, pageBg } from './editor/EditableBits';
import { FunnelSections } from './editor/registry';
import { PageWidgets } from './editor/Widgets';
import PlanCompareModal from './PlanCompareModal';
import '../../components/landing/arc/arc.css';

const API = process.env.REACT_APP_BACKEND_URL;

const PLANS = [
  { id: 'launchpad', name: 'Free', price: '$0/mo', who: 'Your first client', note: '1 client sub-account' },
  { id: 'starter', name: 'Starter', price: '$197/mo', who: 'Your first few clients', note: '3 client sub-accounts · BYOK' },
  { id: 'agency', name: 'Agency', price: '$397/mo', who: 'Build a real agency — under YOUR brand', note: '10 client sub-accounts · white-label', popular: true },
  { id: 'pro', name: 'Pro', price: '$897/mo', who: 'Scaling mode', note: 'Unlimited sub-accounts · white-label' },
];

/* The functional activation slot — copy comes from the editable vip_main props */
const VipSlot = ({ vp, state, info, resumeEmail, otp, setOtp, otpSent, sendResumeOtp, verifyResume, password, setPassword, plan, setPlan, busy, activate, showCompare, setShowCompare }) => {
  const { config } = useContext(PageCtx);
  const rich = (v, strongClass) => renderRich(v && v.t, config, strongClass || 'font-bold text-[#141414]');
  return (
    <>
      {state === 'loading' && (
        <div className="py-10 text-center">
          <div className="w-8 h-8 border-2 border-[#141414]/20 border-t-[#141414] rounded-full animate-spin mx-auto mb-3" />
          <p className="arc-body text-sm text-[#141414]/50">Confirming your payment…</p>
        </div>
      )}

      {state === 'error' && (
        <div className="py-8 text-center">
          <p className="arc-body text-sm text-[#141414]/60 mb-4">We couldn't confirm the payment yet. Refresh in a moment, or contact support.</p>
        </div>
      )}

      {state === 'account_exists' && (
        <div className="py-8 text-center" data-testid="vip-account-exists">
          <p className="arc-body text-base text-[#141414]/70 mb-5">Your account is already set up.</p>
          <Link to="/login" className="arc-body inline-block rounded-full bg-[#141414] text-[#F0EFEC] font-bold px-8 py-3.5 hover:bg-black transition-colors">Sign in →</Link>
        </div>
      )}

      {state === 'resume' && (
        <div data-testid="vip-resume-flow">
          {!otpSent ? (
            <div className="text-center py-6">
              <p className="arc-body text-sm text-[#141414]/65 mb-5">We'll send a quick verification code to <span className="font-bold text-[#141414]">{resumeEmail}</span> to pick up where you left off.</p>
              <button onClick={sendResumeOtp} disabled={busy} className="arc-body rounded-full bg-[#141414] text-[#F0EFEC] font-bold px-8 py-3.5 hover:bg-black disabled:opacity-50 transition-colors" data-testid="vip-send-otp-btn">
                {busy ? 'Sending…' : 'Send my code'}
              </button>
            </div>
          ) : (
            <form onSubmit={verifyResume} className="space-y-4 py-2">
              <input type="text" inputMode="numeric" maxLength={6} placeholder="6-digit code" value={otp} onChange={(e) => setOtp(e.target.value)} className="arc-body w-full px-4 py-3.5 rounded-xl bg-white border border-black/[0.12] text-[#141414] text-center text-xl font-bold tracking-[0.4em] focus:border-[#141414]/40 focus:outline-none" data-testid="vip-otp-input" />
              <button type="submit" disabled={busy || otp.length !== 6} className="arc-body w-full rounded-full bg-[#141414] text-[#F0EFEC] font-bold px-8 py-3.5 hover:bg-black disabled:opacity-40 transition-colors" data-testid="vip-verify-otp-btn">
                {busy ? 'Verifying…' : 'Verify & continue'}
              </button>
            </form>
          )}
        </div>
      )}

      {state === 'ready' && (
        <form onSubmit={activate} className="space-y-5" data-testid="vip-activation-form">
          <div>
            <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Your login email</label>
            <input type="email" value={info.email} readOnly className="arc-body w-full px-4 py-3 rounded-xl bg-black/[0.04] border border-black/[0.08] text-[#141414]/70 font-medium" data-testid="vip-email-display" />
          </div>
          <div>
            <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-1.5">Create your password</label>
            <div className="relative">
              <Lock size={15} className="absolute left-4 top-1/2 -translate-y-1/2 text-[#141414]/35" />
              <input type="password" required minLength={8} placeholder="Min. 8 characters" value={password} onChange={(e) => setPassword(e.target.value)} className="arc-body w-full pl-10 pr-4 py-3 rounded-xl bg-white border border-black/[0.12] text-[#141414] font-medium focus:border-[#141414]/40 focus:outline-none" data-testid="vip-password-input" />
            </div>
          </div>
          <div>
            <label className="arc-body block text-sm font-bold text-[#141414]/70 mb-2">{rich(vp.plan_label)}</label>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-2.5">
              {PLANS.map((p) => (
                <button type="button" key={p.id} onClick={() => setPlan(p.id)} className={`text-left rounded-xl border p-3.5 transition-colors ${plan === p.id ? 'border-[#141414] bg-[#141414]/[0.04] shadow-sm' : 'border-black/[0.08] hover:border-black/25'}`} data-testid={`vip-plan-${p.id}`}>
                  <div className="flex items-center justify-between mb-0.5">
                    <span className="arc-head font-black text-sm text-[#141414]">{p.name}</span>
                    {plan === p.id ? <Check size={14} className="text-[#141414]" /> : p.popular ? <span className="arc-body text-[9px] font-bold tracking-wide bg-[#141414] text-[#F0EFEC] rounded-full px-2 py-0.5">RECOMMENDED</span> : null}
                  </div>
                  <div className="arc-body text-sm font-bold text-[#141414]/70">{p.price}</div>
                  <div className="arc-body text-[11px] font-semibold text-[#141414]/70 mt-1">{p.who}</div>
                  <div className="arc-body text-[11px] text-[#141414]/45">{p.note}</div>
                </button>
              ))}
            </div>
            <p className="arc-body text-xs text-[#141414]/55 leading-relaxed mt-3" data-testid="vip-plans-includes">
              {rich(vp.includes_text)}
            </p>
            <p className="arc-body text-xs text-[#141414]/45 leading-relaxed mt-2">
              Switch or cancel anytime during the trial — most start on Agency and adjust after their first client. <button type="button" onClick={() => setShowCompare(true)} className="underline underline-offset-2 font-bold text-[#141414]/70 hover:text-[#141414]" data-testid="vip-compare-plans-link">Compare full plans →</button>
            </p>
            <PlanCompareModal open={showCompare} onClose={() => setShowCompare(false)} />
          </div>
          <div className="rounded-xl bg-black/[0.04] border border-black/[0.06] px-4 py-3" data-testid="vip-why-card">
            <p className="arc-body text-xs text-[#141414]/65 leading-relaxed">{rich(vp.why_card)}</p>
          </div>
          <button type="submit" disabled={busy} className="arc-body w-full rounded-full bg-[#141414] text-[#F0EFEC] text-base font-bold px-8 py-4 hover:bg-black disabled:opacity-50 transition-colors shadow-[0_12px_40px_rgba(20,20,20,0.25)]" data-testid="vip-activate-btn">
            {busy ? 'Setting up…' : (rich(vp.cta_label, 'font-black') || 'Start my free 30 days →')}
          </button>
          <div className="text-center">
            <p className="arc-body text-xs text-[#141414]/50 inline-flex items-center gap-1.5 justify-center">
              <ShieldCheck size={12} /> {rich(vp.trust_line)}
            </p>
            <p className="arc-body text-[11px] text-[#141414]/40 mt-1">{rich(vp.subnote)}</p>
          </div>
        </form>
      )}
    </>
  );
};

export default function ChallengeVipPage() {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const resumeEmail = searchParams.get('resume');
  const config = useFunnelConfig();
  const pages = useFunnelContent();
  const { register } = useAuth();

  const [state, setState] = useState(resumeEmail ? 'resume' : 'loading'); // loading | resume | ready | account_exists | error
  const [info, setInfo] = useState({ name: '', email: '', session_id: sessionId });
  const [otp, setOtp] = useState('');
  const [password, setPassword] = useState('');
  const [plan, setPlan] = useState('agency');
  const [busy, setBusy] = useState(false);
  const [showCompare, setShowCompare] = useState(false);
  const [otpSent, setOtpSent] = useState(false);

  const fireConfetti = useCallback(() => {
    confetti({ particleCount: 140, spread: 80, origin: { y: 0.6 }, colors: ['#141414', '#8A8578', '#D8D2C8', '#F0EFEC'] });
    setTimeout(() => confetti({ particleCount: 80, spread: 120, origin: { y: 0.4 }, colors: ['#141414', '#B7B1A4', '#F0EFEC'] }), 400);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (!sessionId) return;
    let attempts = 0;
    const check = async () => {
      try {
        const r = await axios.get(`${API}/api/funnel/kit/status/${sessionId}`);
        if (r.data.status === 'paid') {
          setInfo({ name: r.data.name, email: r.data.email, session_id: sessionId });
          setState(r.data.account_exists ? 'account_exists' : 'ready');
          fireConfetti();
        } else if (attempts < 5) {
          attempts += 1;
          setTimeout(check, 2000);
        } else {
          setState('error');
        }
      } catch {
        setState('error');
      }
    };
    check();
  }, [sessionId, fireConfetti]);

  const sendResumeOtp = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/api/funnel/resume`, { email: resumeEmail });
      setOtpSent(true);
      toast.success('Verification code sent to your email');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Could not send code');
    } finally {
      setBusy(false);
    }
  };

  const verifyResume = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      const r = await axios.post(`${API}/api/funnel/resume/verify`, { email: resumeEmail, otp_code: otp });
      setInfo({ name: r.data.name, email: r.data.email, session_id: r.data.session_id });
      setState('ready');
      fireConfetti();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Invalid code');
    } finally {
      setBusy(false);
    }
  };

  const activate = async (e) => {
    e.preventDefault();
    if (password.length < 8) {
      toast.error('Password must be at least 8 characters');
      return;
    }
    setBusy(true);
    try {
      const result = await register(info.name, info.email, password, plan, info.session_id);
      if (result.requiresCheckout) {
        toast.success('Card on file — $0 today. Redirecting…');
        window.location.href = result.checkoutUrl;
      } else {
        window.location.href = '/dashboard';
      }
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Setup failed — try again');
      setBusy(false);
    }
  };

  const vp = sectionProps(pages.vip, 'vip_main') || {};
  const jobs = sectionProps(pages.thanks, 'three_jobs') || {};

  const vipSlot = (
    <VipSlot
      vp={vp} state={state} info={info} resumeEmail={resumeEmail}
      otp={otp} setOtp={setOtp} otpSent={otpSent} sendResumeOtp={sendResumeOtp} verifyResume={verifyResume}
      password={password} setPassword={setPassword} plan={plan} setPlan={setPlan}
      busy={busy} activate={activate} showCompare={showCompare} setShowCompare={setShowCompare}
    />
  );

  return (
    <PageCtx.Provider value={{ config, handlers: { vipSlot }, jobs }}>
      <div className="min-h-screen arc-bone pb-6" style={{ background: pageBg(pages.vip) || undefined }} data-testid="challenge-vip-page">
        <FunnelSections sections={pages.vip} />
        <PageWidgets sections={pages.vip} />

        {config.test_mode && (
          <div className="fixed bottom-4 left-4 z-50 arc-body rounded-full bg-[#141414] text-[#F0EFEC] text-xs font-bold px-4 py-2 shadow-lg" data-testid="funnel-test-mode-chip">
            TEST MODE — card 4242 4242 4242 4242
          </div>
        )}
      </div>
    </PageCtx.Provider>
  );
}
